<script>
/**
 * @component PlanDetail
 * @description Detalle de un plan con tabs: Datos generales y Coberturas.
 *              Permite editar el plan y gestionar las prestaciones cubiertas.
 * @example <PlanDetail />
 */
import planService from "@/services/plan";
import planCoverageService from "@/services/plan-coverage";
import practiceCatalogService from "@/services/practice-catalog";
import Spinner from "@/components/ui/Spinner";
import debounce from "debounce";
import { mapGetters } from "vuex";

const DEBOUNCE_MS = 500;

const TYPE_OPTIONS = [
  { value: "prepaid", label: "Prepaga" },
  { value: "social", label: "Obra Social" },
  { value: "pmo", label: "PMO" },
  { value: "complementary", label: "Complementario" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Activo" },
  { value: "inactive", label: "Inactivo" },
];

export default {
  name: "PlanDetail",

  components: { Spinner },

  data() {
    return {
      activeTab: "general",
      isLoading: false,
      isSaving: false,
      plan: null,
      form: {
        code: "",
        name: "",
        description: "",
        type: "social",
        monthlyFee: 0,
        status: "active",
        healthInsuranceName: "",
      },
      formErrors: {},
      typeOptions: TYPE_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      // Coberturas
      coverages: [],
      coverageTotal: 0,
      coveragePage: 1,
      coveragePageSize: 20,
      coverageLoading: false,
      coverageSearch: "",
      /** @type {string} Categoría seleccionada para filtrar coberturas */
      coverageCategory: "",
      // Dialog cobertura
      isCoverageDialogVisible: false,
      coverageDialogMode: "create",
      editingCoverageId: null,
      coverageForm: {
        practice: "",
        practiceLabel: "",
        isCovered: true,
        coveragePercent: 100,
        hasCopago: false,
        copagoAmount: 0,
        hasCoseguro: false,
        coseguroPercent: 0,
        hasAnnualLimit: false,
        annualLimit: null,
        hasMonthlyLimit: false,
        monthlyLimit: null,
        requiresAuth: false,
        authByAudit: false,
        hasWaitingPeriod: false,
        waitingPeriodMonths: null,
        waitingPeriodNote: "",
        notes: "",
      },
      coverageFormErrors: {},
      // Buscador de prestaciones
      practiceOptions: [],
      practiceSearchLoading: false,
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),

    /**
     * @description ID del plan desde la ruta
     * @returns {string}
     */
    planId() {
      return this.$route.params.id;
    },
  },

  created() {
    this.fetchPlan();
  },

  methods: {
    /**
     * @description Carga los datos del plan
     */
    async fetchPlan() {
      this.isLoading = true;
      try {
        const data = await planService.getById(this.planId);
        this.plan = data;
        this.form = {
          code: data.code || "",
          name: data.name || "",
          description: data.description || "",
          type: data.type || "social",
          monthlyFee: data.monthlyFee || 0,
          status: data.status || "active",
          healthInsuranceName: data.healthInsuranceName || "",
        };
        this.fetchCoverages();
      } catch (error) {
        this.$message.error("Error al cargar plan");
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Guarda los datos generales del plan
     */
    async savePlan() {
      this.formErrors = {};
      if (!this.form.name) this.formErrors.name = "Nombre requerido";
      if (Object.keys(this.formErrors).length) return;

      this.isSaving = true;
      try {
        await planService.update(this.planId, this.form);
        this.$message.success("Plan actualizado");
      } catch (error) {
        this.$message.error("Error al guardar plan");
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * @description Elimina el plan y vuelve al listado
     */
    async deletePlan() {
      try {
        await this.$confirm(
          `¿Eliminar el plan "${this.form.name}"?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await planService.remove(this.planId);
        this.$message.success("Plan eliminado");
        this.$router.push({ name: "plans" });
      } catch {
        // Cancelado
      }
    },

    // --- Coberturas ---

    /**
     * @description Obtiene las coberturas del plan
     */
    async fetchCoverages() {
      this.coverageLoading = true;
      try {
        const params = {
          plan: this.planId,
          offset: (this.coveragePage - 1) * this.coveragePageSize,
          limit: this.coveragePageSize,
        };
        if (this.coverageSearch) params.searchInput = this.coverageSearch;
        if (this.coverageCategory) params.category = this.coverageCategory;

        const result = await planCoverageService.query(params);
        this.coverages = result.docs || [];
        this.coverageTotal = result.total || 0;
      } catch (error) {
        this.$message.error("Error al cargar coberturas");
        console.error(error);
      } finally {
        this.coverageLoading = false;
      }
    },

    debounceCoverageSearch: debounce(function () {
      this.coveragePage = 1;
      this.fetchCoverages();
    }, DEBOUNCE_MS),

    /**
     * @method onCoveragePageChange
     * @desc Cambia la página y recarga las coberturas
     * @param {number} page
     */
    onCoveragePageChange(page) {
      this.coveragePage = page;
      this.fetchCoverages();
    },

    /**
     * @description Busca prestaciones en el catálogo para el selector
     * @param {string} query - Texto de búsqueda
     */
    searchPractices: debounce(function (query) {
      if (!query || query.length < 2) {
        this.practiceOptions = [];
        return;
      }
      this.practiceSearchLoading = true;
      practiceCatalogService
        .query({ searchInput: query, limit: 20 })
        .then((result) => {
          this.practiceOptions = (result.docs || []).map((p) => ({
            value: p._id,
            label: `${p.code || ""} - ${p.name}`,
          }));
        })
        .catch(() => {
          this.practiceOptions = [];
        })
        .finally(() => {
          this.practiceSearchLoading = false;
        });
    }, 300),

    openCoverageDialog(mode = "create", coverage = null) {
      this.coverageDialogMode = mode;
      this.isCoverageDialogVisible = true;
      this.coverageFormErrors = {};
      this.practiceOptions = [];

      if (mode === "edit" && coverage) {
        this.editingCoverageId = coverage._id;
        const pLabel =
          coverage.practice && typeof coverage.practice === "object"
            ? `${coverage.practice.code || ""} - ${coverage.practice.name}`
            : "";
        const pId =
          coverage.practice && typeof coverage.practice === "object"
            ? coverage.practice._id
            : coverage.practice;

        if (pLabel) {
          this.practiceOptions = [{ value: pId, label: pLabel }];
        }

        this.coverageForm = {
          practice: pId || "",
          practiceLabel: pLabel,
          isCovered: coverage.isCovered ?? true,
          coveragePercent: coverage.coveragePercent ?? 100,
          hasCopago: coverage.hasCopago || false,
          copagoAmount: coverage.copagoAmount ?? 0,
          hasCoseguro: coverage.hasCoseguro || false,
          coseguroPercent: coverage.coseguroPercent ?? 0,
          hasAnnualLimit: coverage.hasAnnualLimit || false,
          annualLimit: coverage.annualLimit ?? null,
          hasMonthlyLimit: coverage.hasMonthlyLimit || false,
          monthlyLimit: coverage.monthlyLimit ?? null,
          requiresAuth: coverage.requiresAuth || false,
          authByAudit: coverage.authByAudit || false,
          hasWaitingPeriod: coverage.hasWaitingPeriod || false,
          waitingPeriodMonths: coverage.waitingPeriodMonths ?? null,
          waitingPeriodNote: coverage.waitingPeriodNote || "",
          notes: coverage.notes || "",
        };
      } else {
        this.resetCoverageForm();
      }
    },

    closeCoverageDialog() {
      this.isCoverageDialogVisible = false;
      this.resetCoverageForm();
    },

    resetCoverageForm() {
      this.editingCoverageId = null;
      this.coverageForm = {
        practice: "",
        practiceLabel: "",
        isCovered: true,
        coveragePercent: 100,
        hasCopago: false,
        copagoAmount: 0,
        hasCoseguro: false,
        coseguroPercent: 0,
        hasAnnualLimit: false,
        annualLimit: null,
        hasMonthlyLimit: false,
        monthlyLimit: null,
        requiresAuth: false,
        authByAudit: false,
        hasWaitingPeriod: false,
        waitingPeriodMonths: null,
        waitingPeriodNote: "",
        notes: "",
      };
      this.coverageFormErrors = {};
      this.practiceOptions = [];
    },

    async saveCoverage() {
      this.coverageFormErrors = {};
      if (!this.coverageForm.practice)
        this.coverageFormErrors.practice = "Prestación requerida";
      if (Object.keys(this.coverageFormErrors).length) return;

      const payload = {
        plan: this.planId,
        practice: this.coverageForm.practice,
        isCovered: this.coverageForm.isCovered,
        coveragePercent: this.coverageForm.coveragePercent,
        hasCopago: this.coverageForm.hasCopago,
        copagoAmount: this.coverageForm.copagoAmount,
        hasCoseguro: this.coverageForm.hasCoseguro,
        coseguroPercent: this.coverageForm.coseguroPercent,
        hasAnnualLimit: this.coverageForm.hasAnnualLimit,
        annualLimit: this.coverageForm.annualLimit,
        hasMonthlyLimit: this.coverageForm.hasMonthlyLimit,
        monthlyLimit: this.coverageForm.monthlyLimit,
        requiresAuth: this.coverageForm.requiresAuth,
        authByAudit: this.coverageForm.authByAudit,
        hasWaitingPeriod: this.coverageForm.hasWaitingPeriod,
        waitingPeriodMonths: this.coverageForm.waitingPeriodMonths,
        waitingPeriodNote: this.coverageForm.waitingPeriodNote,
        notes: this.coverageForm.notes,
      };

      try {
        if (this.coverageDialogMode === "create") {
          await planCoverageService.create(payload);
          this.$message.success("Cobertura agregada");
        } else {
          await planCoverageService.update(this.editingCoverageId, payload);
          this.$message.success("Cobertura actualizada");
        }
        this.closeCoverageDialog();
        this.fetchCoverages();
      } catch (error) {
        this.$message.error("Error al guardar cobertura");
      }
    },

    async deleteCoverage(coverage) {
      try {
        await this.$confirm("¿Eliminar esta cobertura?", "Eliminar", {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          type: "warning",
        });
        await planCoverageService.remove(coverage._id);
        this.$message.success("Cobertura eliminada");
        this.fetchCoverages();
      } catch {
        // Cancelado
      }
    },

    /**
     * @description Devuelve el nombre de la prestación desde el objeto populate
     * @param {Object|string} practice - Prestación
     * @returns {string}
     */
    getPracticeName(practice) {
      if (!practice) return "-";
      if (typeof practice === "object") return practice.name || "-";
      return practice;
    },

    /**
     * @description Devuelve el código de la prestación
     * @param {Object|string} practice - Prestación
     * @returns {string}
     */
    getPracticeCode(practice) {
      if (!practice) return "-";
      if (typeof practice === "object") return practice.code || "-";
      return "-";
    },

    /**
     * @description Importa prestaciones desde el catálogo maestro al plan
     * @returns {Promise<void>}
     */
    async importFromCatalog() {
      try {
        await this.$confirm(
          "Se importarán todas las prestaciones del catálogo al plan, pre-completando cobertura desde datos PMO. Las coberturas ya existentes no se sobreescriben.",
          "Importar desde catálogo",
          {
            confirmButtonText: "Importar",
            cancelButtonText: "Cancelar",
            type: "info",
          }
        );
        this.coverageLoading = true;
        const result = await planCoverageService.importFromCatalog(
          this.planId,
          this.plan.organization
        );
        this.$message.success(
          `${result.imported} prestaciones importadas, ${result.skipped} ya existían`
        );
        this.fetchCoverages();
      } catch {
        // Cancelado
      } finally {
        this.coverageLoading = false;
      }
    },

    /**
     * @description Vuelve al listado de planes
     */
    goBack() {
      this.$router.push({ name: "plans" });
    },
  },
};
</script>

<template lang="pug">
section
  //- Header
  .detail-header
    .detail-header__back(@click="goBack")
      micon(name="arrow_back")
      span Planes
    .detail-header__title {{ plan ? plan.name : 'Cargando...' }}
    .detail-header__actions(v-if="isAdmin")
      button.button.button--ghost(@click="deletePlan")
        micon(name="delete_forever")
        span Eliminar

  spinner(v-if="isLoading")

  template(v-else-if="plan")
    //- Tabs
    .detail-tabs
      .detail-tabs__item(
        :class="{ 'is-active': activeTab === 'general' }"
        @click="activeTab = 'general'"
      ) Datos generales
      .detail-tabs__item(
        :class="{ 'is-active': activeTab === 'coverages' }"
        @click="activeTab = 'coverages'"
      ) Coberturas

    //- Tab: Datos generales
    .box.box--with-subnav(v-if="activeTab === 'general'")
      .box__content--stretch
        el-form.detail-form(label-position="top" size="small")
          el-row(:gutter="16")
            el-col(:span="8")
              el-form-item(label="Código")
                el-input(v-model="form.code" maxlength="20" placeholder="Ej: PLAN-001")
            el-col(:span="16")
              el-form-item(label="Nombre" required :error="formErrors.name")
                el-input(v-model="form.name" maxlength="100" placeholder="Ej: Plan 500 Gold")
          el-form-item(label="Descripción")
            el-input(
              type="textarea"
              v-model="form.description"
              :rows="3"
              maxlength="500"
              placeholder="Descripción del plan de cobertura"
            )
          el-row(:gutter="16")
            el-col(:span="8")
              el-form-item(label="Tipo")
                el-select(v-model="form.type" style="width: 100%" placeholder="Seleccionar tipo")
                  el-option(
                    v-for="opt in typeOptions"
                    :key="opt.value"
                    :label="opt.label"
                    :value="opt.value"
                  )
            el-col(:span="8")
              el-form-item(label="Cuota Mensual")
                el-input-number(v-model="form.monthlyFee" :min="0" :precision="2" style="width: 100%")
            el-col(:span="8")
              el-form-item(label="Estado")
                el-select(v-model="form.status" style="width: 100%" placeholder="Seleccionar estado")
                  el-option(
                    v-for="opt in statusOptions"
                    :key="opt.value"
                    :label="opt.label"
                    :value="opt.value"
                  )
          el-form-item(label="Denominación del seguro de salud")
            el-input(v-model="form.healthInsuranceName" placeholder="Ej: OSDE, Swiss Medical, PAMI...")
          .detail-form__actions
            el-button(type="primary" @click="savePlan" :loading="isSaving") Guardar

    //- Tab: Coberturas
    template(v-if="activeTab === 'coverages'")
      header.headline
        .headline__actions
          .coverage-filters
            el-input(
              v-model="coverageSearch"
              placeholder="Buscar prestación"
              prefix-icon="el-icon-search"
              clearable
              size="small"
              style="width: 240px"
              @input="debounceCoverageSearch"
              @clear="debounceCoverageSearch"
            )
            el-select(
              v-model="coverageCategory"
              placeholder="Todas las categorías"
              clearable
              size="small"
              style="width: 200px"
              @change="() => { coveragePage = 1; fetchCoverages() }"
            )
              el-option(label="Consulta" value="consultation")
              el-option(label="Práctica" value="practice")
              el-option(label="Laboratorio" value="laboratory")
              el-option(label="Imagen" value="imaging")
              el-option(label="Cirugía" value="surgery")
              el-option(label="Internación" value="hospitalization")
              el-option(label="Medicación" value="medication")
              el-option(label="Otro" value="other")
          .button.button--ghost(@click="importFromCatalog" v-if="isAdmin")
            micon(name="cloud_download").button__icon
            span.button__text Importar desde catálogo
          .button.button--navy(@click="openCoverageDialog('create')" v-if="isAdmin")
            micon(name="add_circle_outline").button__icon
            span.button__text Agregar cobertura

      .box.box--with-subnav
        .box__content--stretch
          table
            thead
              tr
                th Código
                th Prestación
                th Cubierta
                th % Cobertura
                th Copago
                th Coseguro
                th Autorización
                th Tope anual
                th Carencia
                th Acciones
            tbody
              spinner(v-if="coverageLoading")
              tr(
                v-else
                v-for="c in coverages"
                :key="c._id"
                @click="openCoverageDialog('edit', c)"
              )
                td {{ getPracticeCode(c.practice) }}
                td {{ getPracticeName(c.practice) }}
                td
                  span.badge(:class="c.isCovered ? 'badge--success' : 'badge--danger'")
                    | {{ c.isCovered ? 'Sí' : 'No' }}
                td {{ c.isCovered ? (c.coveragePercent + '%') : '-' }}
                td {{ c.hasCopago ? ('$' + (c.copagoAmount || 0).toLocaleString('es-AR')) : '-' }}
                td {{ c.hasCoseguro ? (c.coseguroPercent + '%') : '-' }}
                td
                  span(v-if="c.requiresAuth")
                    span.badge.badge--warning {{ c.authByAudit ? 'Auditoría' : 'Sí' }}
                  span(v-else) -
                td {{ c.hasAnnualLimit ? c.annualLimit : '-' }}
                td {{ c.hasWaitingPeriod ? (c.waitingPeriodMonths + ' meses') : '-' }}
                td.actions
                  .actions-container(@click.stop)
                    button.button.button--action(@click="openCoverageDialog('edit', c)")
                      micon(name="edit")
                    button.button.button--action(@click="deleteCoverage(c)" v-if="isAdmin")
                      micon(name="delete_forever")

        .coverage-pagination(style="margin-top: 16px; text-align: center")
          el-pagination(
            v-if="coverageTotal > 0"
            layout="prev, pager, next, total"
            :total="coverageTotal"
            :page-size="coveragePageSize"
            :current-page="coveragePage"
            @current-change="onCoveragePageChange"
          )

  //- Dialog cobertura
  el-dialog(
    :title="coverageDialogMode === 'create' ? 'Agregar Cobertura' : 'Editar Cobertura'"
    :visible.sync="isCoverageDialogVisible"
    width="680px"
  )
    el-form(label-position="top" size="small")

      .form-card
        .form-card__title Prestación
        el-form-item(label="Prestación" required :error="coverageFormErrors.practice")
          el-select(
            v-model="coverageForm.practice"
            filterable remote reserve-keyword
            placeholder="Buscar por código o nombre..."
            :remote-method="searchPractices"
            :loading="practiceSearchLoading"
            style="width:100%"
            :disabled="coverageDialogMode === 'edit'"
          )
            el-option(v-for="opt in practiceOptions" :key="opt.value" :label="opt.label" :value="opt.value")
        el-form-item(label=" ")
          el-checkbox(v-model="coverageForm.isCovered") Prestación cubierta por este plan

      .form-card
        .form-card__title Cobertura y cargos
        el-row(:gutter="12")
          el-col(:span="8")
            el-form-item(label="% Cobertura del plan")
              el-input-number(v-model="coverageForm.coveragePercent" :min="0" :max="100" style="width:100%" :disabled="!coverageForm.isCovered")
          el-col(:span="8")
            el-form-item(label=" ")
              el-checkbox(v-model="coverageForm.hasCopago") Tiene copago
          el-col(:span="8")
            el-form-item(label="Monto copago $")
              el-input-number(v-model="coverageForm.copagoAmount" :min="0" :precision="2" style="width:100%" :disabled="!coverageForm.hasCopago")
        el-row(:gutter="12")
          el-col(:span="8")
            el-form-item(label=" ")
              el-checkbox(v-model="coverageForm.hasCoseguro") Tiene coseguro
          el-col(:span="8")
            el-form-item(label="% Coseguro afiliado")
              el-input-number(v-model="coverageForm.coseguroPercent" :min="0" :max="100" style="width:100%" :disabled="!coverageForm.hasCoseguro")

      .form-card
        .form-card__title Autorización
        el-row(:gutter="12")
          el-col(:span="12")
            el-form-item(label=" ")
              el-checkbox(v-model="coverageForm.requiresAuth") Requiere autorización previa
          el-col(:span="12")
            el-form-item(label=" ")
              el-checkbox(v-model="coverageForm.authByAudit" :disabled="!coverageForm.requiresAuth") Por control de auditoría médica

      .form-card
        .form-card__title Topes
        el-row(:gutter="12")
          el-col(:span="8")
            el-form-item(label=" ")
              el-checkbox(v-model="coverageForm.hasAnnualLimit") Tope anual
          el-col(:span="8")
            el-form-item(label="Cantidad por año")
              el-input-number(v-model="coverageForm.annualLimit" :min="0" style="width:100%" :disabled="!coverageForm.hasAnnualLimit")
          el-col(:span="8")
        el-row(:gutter="12")
          el-col(:span="8")
            el-form-item(label=" ")
              el-checkbox(v-model="coverageForm.hasMonthlyLimit") Tope mensual
          el-col(:span="8")
            el-form-item(label="Cantidad por mes")
              el-input-number(v-model="coverageForm.monthlyLimit" :min="0" style="width:100%" :disabled="!coverageForm.hasMonthlyLimit")

      .form-card
        .form-card__title Carencia
        el-row(:gutter="12")
          el-col(:span="8")
            el-form-item(label=" ")
              el-checkbox(v-model="coverageForm.hasWaitingPeriod") Tiene carencia
          el-col(:span="8")
            el-form-item(label="Meses de carencia")
              el-input-number(v-model="coverageForm.waitingPeriodMonths" :min="0" style="width:100%" :disabled="!coverageForm.hasWaitingPeriod")
          el-col(:span="24")
            el-form-item(label="Nota de carencia")
              el-input(v-model="coverageForm.waitingPeriodNote" placeholder="Condiciones o excepciones" :disabled="!coverageForm.hasWaitingPeriod")

      .form-card
        .form-card__title Notas
        el-form-item(label="Observaciones")
          el-input(type="textarea" v-model="coverageForm.notes" :rows="2")

    template(#footer)
      el-button(@click="closeCoverageDialog") Cancelar
      el-button(type="primary" @click="saveCoverage") Guardar
</template>

<style lang="scss" scoped>
.detail-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;

  &__back {
    display: flex;
    align-items: center;
    gap: 6px;
    color: $text-secondary-new;
    cursor: pointer;
    font-size: 14px;

    .micon {
      fill: $text-secondary-new;
      width: 20px;
      height: 20px;
    }

    &:hover {
      color: $navy;
      .micon {
        fill: $navy;
      }
    }
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: $text-primary-new;
    flex: 1;
  }
}

.detail-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid $border-color;
  margin-bottom: 20px;

  &__item {
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    color: $text-secondary-new;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    transition: all 0.15s ease;

    &:hover {
      color: $text-primary-new;
    }

    &.is-active {
      color: $navy;
      border-bottom-color: $teal;
    }
  }
}

.detail-form {
  padding: 20px;

  &__actions {
    padding-top: 16px;
    display: flex;
    justify-content: flex-end;
  }
}

.headline__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.search__input {
  width: 280px;
}
tbody tr {
  cursor: pointer;
}

.form-card {
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 14px;
}

.form-card__title {
  font-size: 13.5px;
  font-weight: 700;
  color: #0b1f3a;
  margin-bottom: 14px;
}

.coverage-filters {
  display: flex;
  gap: 10px;
  align-items: center;
}
</style>
